import { useLazyQuery, useMutation, useQuery } from "@apollo/client/react";
import paths from "config/routePaths";
import User from "domain/User";
import { GetAuthenticationUser } from "hooks/apollo/authenticationUser/authenticationUser.gql";
import { GetAllCompanies } from "hooks/apollo/company/company.gql";
import { GetAllRoles } from "hooks/apollo/role/role.gql";
import { GetAssignableSites } from "hooks/apollo/site/site.gql";
import { CreateUser, GetUsers } from "hooks/apollo/user/user.gql";
import useGoBack from "hooks/useGoBack";
import React, { useState } from "react";
import Uuid from "utils/Uuid";
import UserEditView from "./UserEditView";

export default function UserCreateContainer({ companyId, siteId }) {
  const { goBack } = useGoBack();
  const [createUser] = useMutation(CreateUser, { onCompleted: () => leavePage() });
  const { data: roleData, loading: rolesLoading } = useQuery(GetAllRoles);
  const { data: companiesData, loading: companiesLoading } = useQuery(GetAllCompanies);
  const initialRole = companyId ? "planner" : undefined;
  const initialCompanyId = companyId;
  const {
    data: sitesData,
    loading: sitesLoading,
    previousData: previousSitesData,
    refetch: refetchSites,
  } = useQuery(GetAssignableSites, {
    variables: { companyId: initialCompanyId, role: initialRole },
  });
  const [authenticationUserExists, setAuthenticationUserExists] = useState(false);
  const [getAuthenticationUser] = useLazyQuery(GetAuthenticationUser, {
    fetchPolicy: "network-only",
  });

  const loading = rolesLoading || companiesLoading || (sitesLoading && !previousSitesData);

  const handleCancel = () => leavePage();

  const handleSave = (args) => {
    const defaultUser = {
      email: "",
      id: Uuid.generate(),
      password: "",
      role: "",
    };

    const userToSave = { ...defaultUser, ...args, companyId: args.company?.id };

    return createUser({ refetchQueries: [{ query: GetUsers }], variables: userToSave });
  };

  const fetchAuthenticationUser = (event) => {
    const email = event.target.value;

    getAuthenticationUser({ variables: { email } }).then((result) => {
      const {
        authenticationUser: { id: authenticationUserId },
      } = result.data;
      if (authenticationUserId) {
        setAuthenticationUserExists(true);
      } else {
        setAuthenticationUserExists(false);
      }
    });
  };

  const leavePage = () => {
    goBack(paths.userIndex);
  };

  if (loading) return null;

  return (
    <UserEditView
      assignableSites={sitesData?.assignableSites || []}
      authenticationUserExists={authenticationUserExists}
      companies={companiesData?.companies || []}
      fetchAuthenticationUser={fetchAuthenticationUser}
      initialCompanyId={initialCompanyId}
      initialRole={initialRole}
      onCancel={handleCancel}
      onSave={handleSave}
      refetchSites={refetchSites}
      roles={roleData?.roles || []}
      siteId={siteId}
      user={User.build()}
    />
  );
}
